import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useHistory, Redirect, RouteComponentProps } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import successAnimation from 'src/public/animations/colorful_confetti.json';
import Lottie from 'react-lottie-player';

import {
    Button as ButtonBase,
    BodyTextMedium,
    HeadingText40,
    HeadingText60 as HeadingText60Base,
    pxToRem,
} from '@modernhealth/vital-components';

import { colors } from '@modernhealth/vital-foundation';

import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { appClient } from 'src/config/apollo';
import { useGetScheduledContentActivityLazyQuery } from 'src/modules/scheduledContent/documents/getScheduledContentActivity.generated';
import { font } from 'src/shared/styles/variables';
import {
    MilestoneWrapper,
    MilestoneBadgeDecorator,
    MilestoneBadge,
} from 'src/modules/scheduledContent/components/MilestoneBadge';

import { DailyPausePageWrapper } from '../components/DailyPausePageWrapper';
import { ReactComponent as StarIcon } from '../assets/star-icon.svg';
import { DailyPauseCelebrationTestId } from 'src/modules/scheduledContent/pages/testIds';
import { getRelevantStreak } from '../util/getRelevantStreak';
import { calculateDailyPauseStreakCTA } from '../util/calculateDailyPauseStreakCTA';

export type DailyPauseCelebrationProps = RouteComponentProps<
    {},
    {},
    | { uuid?: string; dailyPauseRemindersPageVisited?: boolean; hasAchievedMilestone?: boolean }
    | undefined
>;

export const DailyPauseNewCelebration: React.FC<DailyPauseCelebrationProps> = ({ location }) => {
    const { uuid, dailyPauseRemindersPageVisited, hasAchievedMilestone } = location.state || {};

    const { t } = useTranslation();
    const history = useHistory();
    const {
        releaseDiginShowDailyPauseCalendarRemindersWeb,
        releaseDiginShowDailyPauseNotificationsWeb,
    } = useFlags();
    const dailyPauseSettingsEnabled =
        releaseDiginShowDailyPauseCalendarRemindersWeb ||
        releaseDiginShowDailyPauseNotificationsWeb;

    const pastMonth = moment().subtract(1, 'months').format('Y-MM'); // minus 1 month, because in DailyPauseWeekStreak component there can be days from pastMonth

    const [getScheduledContentActivity, { data, loading, error }] =
        useGetScheduledContentActivityLazyQuery({
            client: appClient,
            variables: { from: `${pastMonth}-01` },
        });

    useEffect(() => {
        getScheduledContentActivity();
    }, [getScheduledContentActivity, uuid]);

    const handleDailyPauseDoneRedirect = () => {
        if (dailyPauseRemindersPageVisited || !dailyPauseSettingsEnabled) {
            history.push('/pause/stats');
        }

        if (!dailyPauseRemindersPageVisited && dailyPauseSettingsEnabled) {
            history.push('/pause/additional-options', {
                dailyPauseRemindersPageVisited,
            });
        }
    };

    if (!uuid || error) {
        return <Redirect to="/dashboard" />;
    }

    if (loading || !data?.scheduledContentActivity) {
        return <CenteredSpinner />;
    }

    const relevantStreak = getRelevantStreak(
        data.scheduledContentActivity.dayStreakCount,
        data.scheduledContentActivity.weekStreakCount,
    );

    return (
        <DailyPausePageWrapper
            onRedirect={handleDailyPauseDoneRedirect}
            testId={DailyPauseCelebrationTestId.Container}
        >
            {hasAchievedMilestone ? (
                <>
                    <MilestoneWrapper>
                        <MilestoneBadgeDecorator>
                            <MilestoneStar1 />
                            <MilestoneStar2 fill={colors.yellow10} />
                            <MilestoneStar3 fill={colors.cyan30} />
                            <MilestoneBadge
                                isCompleted
                                size={110}
                                borderWidth={10}
                                children={
                                    <HeadingText60 fontColor="white">
                                        {data.scheduledContentActivity.completedCount}
                                    </HeadingText60>
                                }
                            />
                        </MilestoneBadgeDecorator>
                    </MilestoneWrapper>
                    <HeadingText40 centered marginTop={82} marginBottom={15}>
                        {t('dailyPause:streaks.milestone.title')}
                    </HeadingText40>
                    <BodyTextMedium centered marginBottom={54}>
                        {data.scheduledContentActivity.completedCount === 1
                            ? t('dailyPause:streaks.milestone.description', {
                                  streakLength: data?.scheduledContentActivity.weekStreakCount,
                                  completedCount: t('shared:ordinal.first'),
                              })
                            : t('dailyPause:streaks.milestone.description_plural', {
                                  streakLength: relevantStreak.value,
                                  streakType: t(
                                      relevantStreak.type === 'dayStreak'
                                          ? 'dailyPause:streaks.type.day'
                                          : 'dailyPause:streaks.type.week',
                                  ),
                                  completedCount: data.scheduledContentActivity.completedCount,
                              })}
                    </BodyTextMedium>
                </>
            ) : (
                <>
                    <MilestoneWrapper>
                        <MilestoneBadgeDecorator borderColor="violet05">
                            <MilestoneBadge
                                isCompleted
                                completedColor="violet65"
                                size={110}
                                borderWidth={10}
                                borderColor="violet05"
                                children={
                                    <HeadingText60 fontColor="white">
                                        {relevantStreak.value}
                                    </HeadingText60>
                                }
                            />
                        </MilestoneBadgeDecorator>
                        <SuccessAnimation animationData={successAnimation} loop play />
                    </MilestoneWrapper>
                    <HeadingText40 centered marginTop={82} marginBottom={15}>
                        {t('dailyPause:celebration_modal.be_proud')}
                    </HeadingText40>
                    <BodyTextMedium centered marginBottom={54}>
                        {calculateDailyPauseStreakCTA(
                            data?.scheduledContentActivity.dayStreakCount,
                            data?.scheduledContentActivity.weekStreakCount,
                            t,
                        )}
                    </BodyTextMedium>
                </>
            )}
            <ButtonGroup>
                <Button variant="secondary" size="large" onClick={handleDailyPauseDoneRedirect}>
                    {t('dailyPause:celebration_modal.cta_done')}
                </Button>
            </ButtonGroup>
        </DailyPausePageWrapper>
    );
};

const ButtonGroup = styled.div({
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
});

const Button = styled(ButtonBase)({
    maxWidth: '250px',
    width: '100%',
});

const HeadingText60 = styled(HeadingText60Base)({ fontSize: font.size.headerBig });

const MilestoneStar = styled(StarIcon)({
    display: 'block',
    position: 'absolute',
});

const MilestoneStar1 = styled(MilestoneStar)({
    top: pxToRem(-4),
    right: pxToRem(-66),
    width: pxToRem(34),
    height: pxToRem(34),
});

const MilestoneStar2 = styled(MilestoneStar)({
    bottom: pxToRem(-50),
    left: pxToRem(-8),
    width: pxToRem(30),
    height: pxToRem(30),
});

const MilestoneStar3 = styled(MilestoneStar)({
    bottom: pxToRem(8),
    left: pxToRem(-64),
    width: pxToRem(20),
    height: pxToRem(20),
});

const SuccessAnimation = styled(Lottie)({
    width: 400,
    maxHeight: 220,
    opacity: 0.7,
    position: 'absolute',
    overflow: 'hidden',
    left: 'calc(50% - 200px)',
});
